/**
 * Convert a backend User payload into the camelCase shape the shared
 * `User` type declares. The web type uses `displayName` + `avatarUrl` +
 * `createdAt`; the DRF serializer returns `first_name`/`last_name`/
 * `username` + `avatar` + `created_at`. Without this normaliser, every
 * consumer reads `undefined` and falls back to placeholders or crashes
 * (e.g. `formatDistanceToNow(new Date(undefined))`).
 *
 * Mirrors the post/comment normaliser pattern — field-explicit on
 * purpose so a future backend rename can't silently leak through.
 */
import type { TerritoryCode } from 'shared/api/types';
import type { User } from 'shared/api/types/user';

type RawTerritory = string | { code?: string; name?: string } | null | undefined;

interface RawUser {
  id: string;
  email: string;
  username?: string;
  first_name?: string;
  last_name?: string;
  display_name?: string;
  displayName?: string;
  avatar?: string | null;
  avatar_url?: string | null;
  avatarUrl?: string | null;
  role?: string;
  territory?: RawTerritory;
  territory_code?: string;
  created_at?: string;
  createdAt?: string;
  updated_at?: string;
  updatedAt?: string;
  deactivated_at?: string | null;
  deactivatedAt?: string | null;
  marked_for_deletion_at?: string | null;
  [key: string]: unknown;
}

function deriveDisplayName(raw: RawUser): string {
  if (raw.display_name) return raw.display_name;
  if (raw.displayName) return raw.displayName;
  const fn = (raw.first_name ?? '').trim();
  const ln = (raw.last_name ?? '').trim();
  const combined = `${fn} ${ln}`.trim();
  if (combined) return combined;
  return raw.username ?? '';
}

function deriveTerritory(raw: RawUser): TerritoryCode {
  const t = raw.territory;
  if (typeof t === 'string') return t as TerritoryCode;
  if (t && typeof t === 'object' && typeof t.code === 'string') {
    return t.code as TerritoryCode;
  }
  return (raw.territory_code ?? '') as TerritoryCode;
}

export function normalizeUser(raw: RawUser): User {
  // first_name + last_name are intentionally surfaced alongside the
  // composed `displayName` because the EditProfileScreen needs to bind
  // each input to its own field. Cast via Object.assign so the shared
  // User type (which doesn't declare them) stays untouched.
  return Object.assign(
    {},
    {
      id: raw.id,
      email: raw.email,
      username: raw.username ?? '',
      displayName: deriveDisplayName(raw),
      avatarUrl: raw.avatar_url ?? raw.avatarUrl ?? raw.avatar ?? null,
      role: (raw.role as User['role']) ?? 'member',
      territory: deriveTerritory(raw),
      createdAt: raw.created_at ?? raw.createdAt ?? '',
      updatedAt: raw.updated_at ?? raw.updatedAt ?? raw.created_at ?? raw.createdAt ?? '',
      deactivatedAt: raw.deactivated_at ?? raw.deactivatedAt ?? null,
      marked_for_deletion_at: raw.marked_for_deletion_at ?? null,
    },
    {
      first_name: raw.first_name ?? '',
      last_name: raw.last_name ?? '',
      bio: (raw.bio as string | undefined) ?? '',
      // Snake-case extras surfaced so EditProfileScreen can bind dedicated
      // inputs to each (phone, DOB) and gate the username field on the
      // set-once flag (`username_set_at`). These mirror UserMeSerializer's
      // payload one-to-one — see apps/users/serializers.py.
      phone_number: (raw.phone_number as string | undefined) ?? '',
      date_of_birth: (raw.date_of_birth as string | null | undefined) ?? null,
      username_set_at: (raw.username_set_at as string | null | undefined) ?? null,
    },
  );
}
